import React from "react";
import { motion } from "framer-motion";
import InteractiveShiraImage from "../components/InteractiveShiraImage";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.85, ease: "easeOut" } },
};

const pageTransition = {
  initial: { opacity: 0, scale: 0.98, filter: "blur(8px)" },
  animate: { 
    opacity: 1, 
    scale: 1, 
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
  },
  exit: { 
    opacity: 0, 
    scale: 1.02, 
    filter: "blur(8px)",
    transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }
  },
};

function InfoSection({ title, children }) {
  return (
    <motion.div variants={fadeUp} style={{ flex: 1, minWidth: "200px" }}>
      <h3
        style={{
          fontFamily: "'Garet', sans-serif",
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#8B8BF5",
          margin: "0 0 8px",
        }}
      >
        {title}
      </h3>
      <p
        style={{
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
          fontSize: "0.95rem",
          lineHeight: 1.6,
          color: "#333333",
          margin: 0,
        }}
      >
        {children}
      </p>
    </motion.div>
  );
}

// Contact item component with copy functionality
function ContactItem({ type, value, windowWidth }) {
  const [isHovered, setIsHovered] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const displayValue = type === 'phone' ? value.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : value;
  const copyText = type === 'phone' ? 'Copy number' : 'Copy email';

  return (
    <div
      data-testid={`contact-${type}`}
      onClick={handleCopy}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => { setIsHovered(false); setCopied(false); }}
      style={{
        position: "relative",
        cursor: "pointer",
        fontFamily: "'Garet', sans-serif",
        fontWeight: 700,
        fontSize: windowWidth >= 768 ? "0.72rem" : "0.65rem",
        letterSpacing: "0.02em",
        color: isHovered ? "#BFFF00" : "#1a1a1a",
        transition: "color 0.2s ease",
        whiteSpace: "nowrap",
      }}
    >
      {displayValue}
      
      {/* Tooltip on hover */}
      {isHovered && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginBottom: "8px",
            padding: "6px 12px",
            background: "#1a1a1a",
            color: "#FFFFFF",
            fontSize: "0.6rem",
            fontWeight: 500,
            borderRadius: "4px",
            whiteSpace: "nowrap",
            zIndex: 10,
          }}
        >
          {copied ? "Copied!" : copyText}
          {/* Arrow */}
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid #1a1a1a",
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function About() {
  const [windowWidth, setWindowWidth] = React.useState(1920);

  React.useEffect(() => {
    // Set initial width on mount
    setWindowWidth(window.innerWidth);
    
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDownloadCV = () => {
    // Create a download link for CV
    const link = document.createElement('a');
    link.href = '/cv/shira-cv.pdf'; // You can update this path
    link.download = 'Shira-CV.pdf';
    link.click();
  };

  // Hide image on tablet and mobile (below 1000px)
  const showImage = windowWidth >= 1000;

  // Calculate image size based on screen width (+15% more from previous)
  // Previous: 322, 280, 220 → Now: 370, 322, 253
  const getImageSize = () => {
    if (windowWidth >= 1400) return 370; // Desktop large (+15%)
    if (windowWidth >= 1200) return 322; // Desktop (+15%)
    return 253; // Small desktop (+15%)
  };

  const imageSize = getImageSize();

  return (
    <motion.div
      data-testid="about-page"
      variants={pageTransition}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{
        width: "100vw",
        minHeight: "100vh",
        overflow: "auto",
        position: "relative",
        background: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        paddingLeft: windowWidth >= 1800 
          ? "calc(5vw + 15%)"  // Large screens - shift 15% right
          : windowWidth >= 768 
            ? "clamp(40px, 5vw, 80px)" 
            : "20px",
        paddingRight: windowWidth >= 768 ? "clamp(40px, 5vw, 80px)" : "20px",
        paddingTop: windowWidth >= 768 ? "0" : "80px",
        paddingBottom: windowWidth >= 768 ? "0" : "120px",
      }}
    >
      {/* Content Container - Flex row for text and image */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "clamp(20px, 4vw, 60px)",
          maxWidth: "1400px",
          width: "100%",
        }}
      >
        {/* Left side - Text content */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          style={{
            maxWidth: windowWidth >= 768 ? "700px" : "100%",
            padding: windowWidth >= 768 ? "40px" : "0",
            textAlign: "left",
            flex: "1",
          }}
        >
          {/* Title - 3 lines like home page */}
          <motion.h1
            variants={fadeUp}
            style={{
              fontFamily: "'Garet', sans-serif",
              fontSize: windowWidth >= 768 ? "clamp(1.7rem, 4.25vw, 3rem)" : "1.5rem",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
              margin: "0 0 48px",
              textAlign: "left",
              position: "relative",
            }}
          >
            <span style={{ fontWeight: 700, color: "#0A0A0A", display: "block", whiteSpace: windowWidth >= 500 ? "nowrap" : "normal" }}>
              I'M A DESIGNER OBSESSED
            </span>
            <span style={{ fontWeight: 700, color: "#0A0A0A", display: "block", whiteSpace: windowWidth >= 500 ? "nowrap" : "normal" }}>
              WITH THE SPACE BETWEEN
            </span>
            <span style={{ fontWeight: 400, color: "#8B8BF5", display: "block", whiteSpace: windowWidth >= 500 ? "nowrap" : "normal" }}>
              PROBLEM AND SOLUTION.
            </span>
          </motion.h1>

        {/* Info sections - 2x2 grid on desktop, 1 column on mobile */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: windowWidth >= 600 ? "1fr 1fr" : "1fr",
            gap: windowWidth >= 600 ? "32px 48px" : "24px",
            textAlign: "left",
            marginBottom: "48px",
          }}
        >
          {/* Row 1: Experience & Background */}
          <InfoSection title="Experience">
            6+ years as a versatile designer across web, print, advertising, and branding.
          </InfoSection>

          <InfoSection title="Background">
            B.Des in Visual Communication<br />
            Bezalel Academy of Arts and Design.
          </InfoSection>

          {/* Row 2: Today & Life */}
          <InfoSection title="Today">
            Studio Manager and Art Director at Rize Media, leading a team of designers.
          </InfoSection>

          <InfoSection title="Life">
            Living in Tel Aviv with a slightly crazy Chihuahua.
          </InfoSection>
        </div>

        {/* Contact row - CV button, Email, Phone - spread across full width */}
        <motion.div 
          variants={fadeUp}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: windowWidth >= 768 ? "space-between" : "flex-start",
            gap: windowWidth >= 768 ? "24px" : "16px",
            flexWrap: windowWidth >= 500 ? "nowrap" : "wrap",
            width: "100%",
          }}
        >
          <button
            onClick={handleDownloadCV}
            data-testid="download-cv-button"
            style={{
              fontFamily: "'Garet', sans-serif",
              fontWeight: 700,
              fontSize: "0.72rem",
              letterSpacing: "0.02em",
              textTransform: "uppercase",
              color: "#1a1a1a",
              background: "#BFFF00",
              border: "none",
              borderRadius: "4px",
              padding: "10px 24px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 4px 20px rgba(191, 255, 0, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Download CV
          </button>

          <ContactItem 
            type="email" 
            value="shirayur@gmail.com" 
            windowWidth={windowWidth}
          />

          <ContactItem 
            type="phone" 
            value="0524610757" 
            windowWidth={windowWidth}
          />
        </motion.div>
      </motion.div>

      {/* Right side - Interactive Shira Image - Desktop only */}
      {showImage && (
        <motion.div
          className="shira-image-container"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          style={{
            position: "relative",
            height: `${imageSize}px`,
            width: `${imageSize}px`,
            marginRight: windowWidth >= 1400 ? "40px" : "20px",
            alignSelf: "flex-start",
            marginTop: windowWidth >= 1400 ? "50px" : "40px",
            flexShrink: 0,
          }}
        >
          <InteractiveShiraImage />
        </motion.div>
      )}
    </div>
    </motion.div>
  );
}
